<template>
    <v-dialog
        :model-value="isOpen"
        max-width="600px"
        transition="slide-y-transition"
        eager
        @update:model-value="$emit('update:isOpen', $event)"
        @after-leave="$emit('after-leave')"
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    {{ t('configurations.products.pages.edit.sections.components.addProcesses') }}
                </h1>
            </v-card-title>

            <v-card-text>
                <ChipsMultiselectAutocomplete
                    v-model="selectedProcess"
                    :items="availableProcesses"
                    :label="t('configurations.products.pages.edit.sections.components.selectProcess')"
                    :placeholder="t('configurations.products.pages.edit.sections.components.selectProcess')"
                    item-title="name"
                    item-value="id"
                    return-object
                    variant="outlined"
                    hide-details
                />
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn color="error" variant="text" @click="onClose">
                    {{ t('configurations.products.pages.edit.sections.components.cancel') }}
                </v-btn>
                <v-spacer />
                <v-btn color="success" variant="text" :disabled="!hasSelectedProcess" @click="onSave">
                    {{ t('configurations.products.pages.edit.sections.components.add') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
/*
Organization / ordering applied:
1. Imports
2. Props / Emits
3. Constants
4. Stores & top-level services
5. Reactive state (refs/reactive)
6. Validation rules & setup
7. Computed
8. Handlers / methods
9. Helpers / lifecycle
*/

// 1. Imports
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ChipsMultiselectAutocomplete from '@/components/common/ChipsMultiselectAutocomplete.vue'

// 2. Props / Emits
const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    processes: {
        type: Array,
        default: () => []
    },
    componentProcesses: {
        type: Array,
        default: () => []
    }
})

const emit = defineEmits(['on-save', 'on-close', 'update:isOpen', 'after-leave'])

// 3. Constants
const { t } = useI18n()

// 4. Stores & top-level services
// No stores needed for this dialog

// 5. Reactive state (refs/reactive)
const selectedProcess = ref([])

// 6. Validation rules & setup
// No validation rules needed

// 7. Computed
const hasSelectedProcess = computed(() => {
    return Array.isArray(selectedProcess.value) && selectedProcess.value.length > 0
})

const availableProcesses = computed(() => {
    if (!props.processes || !props.componentProcesses) return []

    try {
        const existingProcessIds = props.componentProcesses.map((cp) => cp.productionProcess?.id).filter(Boolean)
        return props.processes.filter((process) => !existingProcessIds.includes(process.id))
    } catch (error) {
        console.error('Error computing availableProcesses:', error)
        return []
    }
})

// 8. Handlers / methods
const onSave = () => {
    if (!selectedProcess.value) return

    emit('on-save', selectedProcess.value)
    onClose()
}

const onClose = () => {
    selectedProcess.value = []
    emit('on-close')
    emit('update:isOpen', false)
}

// 9. Helpers / lifecycle
// No lifecycle hooks needed
</script>
